import { format, isSameDay, startOfDay, type Locale } from "date-fns";

/** The service window a job is created with: when the pickup may start and must end. */
export type PickupWindow = { from: Date; to: Date };

/** The shortcut row: today, tomorrow and the day after, each at midnight. */
export const shortcutDays = (now: Date): Date[] =>
  [0, 1, 2].map((offset) => {
    const day = startOfDay(now);
    day.setDate(day.getDate() + offset);
    return day;
  });

/**
 * Which shortcut the window sits on, or -1 when none does — a date picked outside the
 * shortcut row leaves all three unselected.
 */
export const selectedShortcutIndex = (window: PickupWindow, days: Date[]): number =>
  days.findIndex((day) => isSameDay(window.from, day) && isSameDay(window.to, day));

/** "28 Дек": the short month without date-fns' trailing dot, capitalised as in the design. */
export const shortcutLabel = (day: Date, locale: Locale): string =>
  format(day, "d MMM", { locale })
    .replace(/\.$/, "")
    .replace(/\s(\S)/, (_, first: string) => ` ${first.toUpperCase()}`);

/** `day` at `hours`:`minutes`. */
export const atTime = (day: Date, hours: number, minutes: number): Date => {
  const next = new Date(day);
  next.setHours(hours, minutes, 0, 0);
  return next;
};

/** Day from `day`, clock from `time`. */
export const onDay = (day: Date, time: Date): Date =>
  atTime(day, time.getHours(), time.getMinutes());

/** The last minute a window can end on `day`. */
const dayEnd = (day: Date): Date => atTime(day, 23, 59);

/** A window starting earlier today starts now instead — the past isn't schedulable. */
const clampToNow = ({ from, to }: PickupWindow, now: Date): PickupWindow => {
  if (!isSameDay(from, now) || from >= now) return { from, to };
  const clamped = new Date(now);
  clamped.setSeconds(0, 0);
  return { from: clamped, to: to < now ? clamped : to };
};

/**
 * A shortcut moves both ends onto its day and keeps their times. Folding a window that ran
 * past midnight onto one day would invert it (22:00 → 02:00), so До ends the day instead.
 */
export const selectShortcut = (window: PickupWindow, day: Date, now: Date): PickupWindow => {
  const from = onDay(day, window.from);
  const to = onDay(day, window.to);
  return clampToNow({ from, to: to < from ? dayEnd(day) : to }, now);
};

/**
 * Moving От past До drags До along, so the window keeps its length and never inverts. A new
 * time never moves a date: dragged by an hour change, До stops at 23:59 of that day.
 */
export const changeFrom = (window: PickupWindow, from: Date, now: Date): PickupWindow => {
  if (from <= window.to) return clampToNow({ from, to: window.to }, now);
  const length = window.to.getTime() - window.from.getTime();
  const dragged = new Date(from.getTime() + length);
  const to = isSameDay(from, window.from) && dragged > dayEnd(from) ? dayEnd(from) : dragged;
  return clampToNow({ from, to }, now);
};

/** До can't end before От. */
export const changeTo = (window: PickupWindow, to: Date, now: Date): PickupWindow =>
  clampToNow({ from: window.from, to: to < window.from ? window.from : to }, now);
